import { useState, type ChangeEvent } from 'react';
import {
  FRECUENCIAS_PAGO,
  formatBp,
  formatMoney,
  hoy,
  newId,
  parseMoney,
  type FrecuenciaPago,
  type Settings,
} from '@abastece/shared';
import { pagoDe, type Empleado } from '@abastece/core';
import { emitirYVer } from '../lib/emitir.js';
import { cada, costoDePago, enOtraUnidad, montoParaEditar, pagoAcordado, type Por } from '../lib/planilla.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo, Interruptor } from './Campo.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

const ETIQUETA_MONTO: Record<Por, Clave> = {
  dia: 'planilla.pagoPorDia',
  semanal: 'planilla.sueldoPorSemana',
  quincenal: 'planilla.sueldoPorQuincena',
  mensual: 'planilla.salarioMensual',
};

/**
 * Hiring somebody, or changing what was agreed with them: a sheet over «Planilla», as every form a
 * button opens (the owner, 2026-10-06).
 *
 * How they are paid (the owner, 2026-10-12: «some people want to be paid weekly, others are going
 * to be paid BY the amount of days they work», and «an option to not pay INSS»): a fixed amount or
 * so much for each day worked; every week, every fifteen days or every month; with INSS or without;
 * with aguinaldo, vacations and severance or without. What that costs the shop is worked out as it
 * is typed, by the books' arithmetic. A change applies from the next run; a run already made stays
 * as it was paid.
 *
 * A fixed pay every week or fifteen days has two boxes, the month's salary and each payday's, and
 * either can be typed: the other is worked out. Picking another schedule, or by the day, keeps the
 * pay and says it for the new one. There was one box, and what it was for followed the schedule: the
 * owner typed C$ 9,000, the salary, with «Quincenal», and the books had C$ 9,000 every fifteen days,
 * twice the salary in every total (2026-10-31: «it double counts it in the total»).
 *
 * The start date is set right here too, for somebody on staff before they were entered: a hire takes
 * the day it is entered unless it is changed, and a run of the days before left them out (2026-10-31).
 */
export function HojaEmpleado({
  empleado,
  config,
  onCerrar,
}: {
  /** Absent, somebody new. */
  empleado?: Empleado;
  config: Settings;
  onCerrar: () => void;
}) {
  const t = useT();
  const antes = empleado ? pagoDe(empleado) : null;
  // What was agreed, in the box it is for (`montoParaEditar`); a new hire's salary, empty.
  const [inicial] = useState(() => {
    const m = empleado ? montoParaEditar(empleado) : null;
    return { texto: m ? (m.monto / 100).toFixed(2) : '', por: m?.por ?? ('mensual' as Por) };
  });

  const [nombre, setNombre] = useState(empleado?.nombre ?? '');
  const [cargo, setCargo] = useState(empleado?.cargo ?? '');
  const [porDia, setPorDia] = useState(antes?.porDia ?? false);
  const [frecuencia, setFrecuencia] = useState<FrecuenciaPago>(antes?.frecuencia ?? 'mensual');
  /** The amount as typed, and what it is for: the box it was typed in. Any other box shows it worked out. */
  const [monto, setMonto] = useState(inicial);
  const [conInss, setConInss] = useState(antes?.conInss ?? true);
  const [conPrestaciones, setConPrestaciones] = useState(antes?.conPrestaciones ?? true);
  const [fechaIngreso, setFechaIngreso] = useState(empleado?.fechaIngreso ?? hoy());
  const [cedula, setCedula] = useState(empleado?.cedula ?? '');
  const [inssNumero, setInssNumero] = useState(empleado?.inssNumero ?? '');
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const cantidad = monto.texto.trim() === '' ? null : parseMoney(monto.texto);
  const acordado = cantidad !== null && cantidad > 0 ? pagoAcordado(cantidad, monto.por, { porDia, frecuencia }) : null;
  // A centavo a month is no pay a week: the books take only an amount.
  const valido = acordado !== null && acordado.salarioMensual > 0 && (acordado.tarifa === null || acordado.tarifa > 0);
  const listo = Boolean(nombre.trim() && cargo.trim() && valido && /^\d{4}-\d{2}-\d{2}$/.test(fechaIngreso));
  /** What a box shows: what was typed in it, or what was typed in another, said for this one. */
  const enCaja = (por: Por) =>
    monto.por === por ? monto.texto : cantidad !== null && cantidad > 0 ? (enOtraUnidad(cantidad, monto.por, por) / 100).toFixed(2) : '';
  const escribirEn = (por: Por) => (ev: ChangeEvent<HTMLInputElement>) => setMonto({ texto: ev.target.value, por });
  // Left as it opened, what was agreed is not sent again: the two figures the books keep stay theirs.
  const pagoIntacto = antes !== null && monto === inicial && porDia === antes.porDia && frecuencia === antes.frecuencia;
  // Taken off the benefits with some already earned: that is still owed, and paid when they leave.
  const acumulado = empleado && !conPrestaciones && (empleado.aguinaldoAcumulado > 0 || empleado.vacacionesAcumuladas > 0);

  // What it costs: a day of it, or a month, as «Costo mensual» counts it. Before an amount, what to type.
  const opciones = { conInss, conPrestaciones };
  const ayuda =
    acordado && valido
      ? t('planilla.leCuesta', {
          costo: porDia
            ? `${formatMoney(costoDePago(acordado.tarifa!, opciones, config))} ${cada('dia')}`
            : `${formatMoney(costoDePago(acordado.salarioMensual, opciones, config))} ${cada('mensual')}`,
        })
      : porDia
        ? t('planilla.ayudaPorDia')
        : frecuencia !== 'mensual'
          ? t('planilla.ayudaDosCampos', { cada: cada(frecuencia) })
          : conInss
            ? t('planilla.ayudaSalario', { tasa: formatBp(config.inssPatronalBp + config.inatecBp) })
            : conPrestaciones
              ? t(config.vacacionesAcumulan ? 'planilla.ayudaSalarioSinInss' : 'planilla.ayudaSalarioSinInssSinVacaciones')
              : undefined;

  async function guardar() {
    if (!listo || !acordado) return;
    const { tarifa, salarioMensual } = acordado;
    const cambiado = empleado && cambios(empleado, tarifa, salarioMensual);
    // Saved as it was: nothing to record.
    if (cambiado && Object.keys(cambiado).length === 0) return onCerrar();
    setError(null);
    setTrabajando(true);
    try {
      const { rechazo } = empleado
        ? await emitirYVer('empleado.actualizado', { empleadoId: empleado.id, ...cambiado })
        : await emitirYVer('empleado.contratado', {
            empleadoId: newId(),
            nombre: nombre.trim(),
            cargo: cargo.trim(),
            salarioMensual,
            fechaIngreso,
            cedula: cedula.trim() || null,
            inssNumero: conInss ? inssNumero.trim() || null : null,
            frecuencia,
            porDia,
            ...(tarifa !== null ? { tarifa } : {}),
            conInss,
            conPrestaciones,
          });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      // Not recorded at all (no session, something the books cannot take): said here, the sheet kept.
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  /** Only what is different, so «Bitácora» says what was changed. */
  function cambios(e: Empleado, tarifa: number | null, salarioMensual: number) {
    const pago = pagoDe(e);
    const nuevo = {
      nombre: nombre.trim(),
      cargo: cargo.trim(),
      cedula: cedula.trim() || null,
      // Off INSS the number is not asked, and what was there stays.
      ...(conInss ? { inssNumero: inssNumero.trim() || null } : {}),
      ...(pagoIntacto ? {} : { salarioMensual, tarifa }),
      frecuencia,
      porDia,
      conInss,
      conPrestaciones,
      fechaIngreso,
    };
    const era: Record<string, unknown> = { ...e, ...pago, cedula: e.cedula ?? null, inssNumero: e.inssNumero ?? null };
    return Object.fromEntries(Object.entries(nuevo).filter(([k, v]) => era[k] !== v));
  }

  const segmento = (elegido: boolean) =>
    `min-w-0 flex-1 rounded-lg px-2 py-2 text-sm transition ${elegido ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`;

  return (
    <Modal
      titulo={empleado ? t('planilla.editarA', { nombre: empleado.nombre }) : t('planilla.contratar')}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          {error && <p className="text-sm text-rose-400">{error}</p>}
          <button type="button" className="btn-primario w-full" disabled={trabajando || !listo} onClick={() => void guardar()}>
            <Ico icono="guardar" />
            {t(trabajando ? 'comun.guardando' : 'comun.guardar')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-planilla-contratar={empleado ? undefined : true} data-planilla-editar={empleado ? true : undefined}>
        <Campo etiqueta={t('comun.nombre')} value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus={!empleado} maxLength={120} />
        <Campo etiqueta={t('planilla.cargo')} value={cargo} onChange={(e) => setCargo(e.target.value)} maxLength={60} />

        <div>
          <span className="etiqueta">{t('planilla.comoSePaga')}</span>
          <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('planilla.comoSePaga')}>
            {[false, true].map((v) => (
              <button
                key={String(v)}
                type="button"
                aria-pressed={porDia === v}
                data-pago={v ? 'por-dia' : 'fijo'}
                data-sin-icono="pestana"
                onClick={() => setPorDia(v)}
                className={segmento(porDia === v)}
              >
                {t(v ? 'planilla.porDia' : 'planilla.sueldoFijo')}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="etiqueta">{t('planilla.cadaCuanto')}</span>
          <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('planilla.cadaCuanto')}>
            {FRECUENCIAS_PAGO.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={frecuencia === f}
                data-frecuencia={f}
                data-sin-icono="pestana"
                onClick={() => setFrecuencia(f)}
                className={segmento(frecuencia === f)}
              >
                {t(`planilla.frecuencia.${f}`)}
              </button>
            ))}
          </div>
        </div>

        {porDia || frecuencia === 'mensual' ? (
          <Campo
            etiqueta={t(ETIQUETA_MONTO[porDia ? 'dia' : 'mensual'])}
            inputMode="decimal"
            className="tabular"
            value={enCaja(porDia ? 'dia' : 'mensual')}
            onChange={escribirEn(porDia ? 'dia' : 'mensual')}
            placeholder="0.00"
            ayuda={ayuda}
          />
        ) : (
          // The month's salary and each payday's, side by side: either typed, the other worked out.
          <div data-salario-y-pago>
            <div className="grid grid-cols-2 items-end gap-3">
              <Campo
                etiqueta={t(ETIQUETA_MONTO.mensual)}
                inputMode="decimal"
                className="tabular"
                value={enCaja('mensual')}
                onChange={escribirEn('mensual')}
                placeholder="0.00"
              />
              <Campo
                etiqueta={t(ETIQUETA_MONTO[frecuencia])}
                inputMode="decimal"
                className="tabular"
                value={enCaja(frecuencia)}
                onChange={escribirEn(frecuencia)}
                placeholder="0.00"
              />
            </div>
            {ayuda && <p className="mt-1.5 text-xs text-slate-500">{ayuda}</p>}
          </div>
        )}

        <div className="-my-2">
          <Interruptor etiqueta={t('planilla.pagaInss')} descripcion={t('planilla.pagaInssAyuda')} checked={conInss} onChange={setConInss} />
          <Interruptor
            etiqueta={t(config.vacacionesAcumulan ? 'planilla.prestaciones' : 'planilla.prestacionesSinVacaciones')}
            descripcion={t('planilla.prestacionesAyuda')}
            checked={conPrestaciones}
            onChange={setConPrestaciones}
          />
        </div>
        {acumulado && (
          <p className="text-xs text-amber-300" data-sigue-debiendo>
            {t('planilla.sigueDebiendose', {
              aguinaldo: formatMoney(empleado.aguinaldoAcumulado),
              vacaciones: formatMoney(empleado.vacacionesAcumuladas),
            })}
          </p>
        )}

        {/* Set right later too (2026-10-31): a hire takes the day it is entered unless it is changed here. */}
        <Campo
          etiqueta={t('planilla.fechaIngreso')}
          type="date"
          value={fechaIngreso}
          onChange={(e) => setFechaIngreso(e.target.value)}
          ayuda={t(empleado ? 'planilla.ayudaIngresoEditar' : 'planilla.ayudaIngreso')}
        />
        {empleado && <p className="text-xs text-slate-500">{t('planilla.desdeLaProxima')}</p>}
        <div className={conInss ? 'grid grid-cols-2 gap-4' : ''}>
          <Campo etiqueta={t('planilla.cedula')} value={cedula} onChange={(e) => setCedula(e.target.value)} maxLength={20} />
          {conInss && <Campo etiqueta={t('planilla.inssNumero')} value={inssNumero} onChange={(e) => setInssNumero(e.target.value)} maxLength={30} />}
        </div>
      </div>
    </Modal>
  );
}
